import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Lottie from "lottie-react";
import apiPaths from "../../../api/endpoints";
import { useRetrieveContent } from "../../hooks/useRetrieveContent";
import AudioPlayer from "../../components/AudioPlayer/AudioPlayer";
import AudioRecorder, {
  AudioRecorderHandle,
} from "./components/AudioRecorder/AudioRecorder";
import DisplayPopUp from "./components/DisplayPopUp/DisplayPopUp";
import CreateWorkPopup from "./components/CreateWorkPopup/CreateWorkPopup";
import ConfirmationPopup from "./components/ConfirmationPopup/ConfirmationPopup";
import SuccessPopup from "../Doodling/components/SuccessPopup/SuccessPopup";
import backButtonJson from "../../appAssets/jsons/back_button.json";
import undoButtonJson from "../../appAssets/jsons/undo_button.json";
import saveButtonJson from "../../appAssets/jsons/saveButton.json";
import leftArrowJson from "../../appAssets/jsons/left_arrow.json";
import rightArrowJson from "../../appAssets/jsons/right_arrow.json";
import promptForwardJson from "../../appAssets/jsons/promptforwardIcon.json";
import { notebook } from "../../appAssets";
import { InputMethod, JournalPrompt, JOURNAL_STRINGS } from "./types";
import { trackEvent } from "../../../mixpanel";
import { TrackingEvents } from "../../Utilities/analytics";
import "./KidsJournal.scss";

const MAX_LINES = 4;
const CHARS_PER_LINE = 65;

function KidsJournal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const navigatedFrom = searchParams.get("navigatedFrom");
  const { getRetrieveContent, postRetrieveContent } = useRetrieveContent();
  const audioRecorderRef = useRef<AudioRecorderHandle>(null);

  // ── State
  const [prompts, setPrompts] = useState<JournalPrompt[]>([]);
  const [promptIdx, setPromptIdx] = useState(0);
  const [inputMethod, setInputMethod] = useState<InputMethod>("");
  const [primaryText, setPrimaryText] = useState("");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [isOwn, setIsOwn] = useState(
    navigatedFrom === "heartcorner" ? true : false,
  );
  const [isShowOwnBtn, setIsShowOwnBtn] = useState(true);
  const [disableControls, setDisableControls] = useState(false);

  // ── Popups
  const [showDisplayPopup, setShowDisplayPopup] = useState(true);
  const [showCreateWork, setShowCreateWork] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // ── Undo history (mobile primaryInputTextHistoryRef)
  const textHistoryRef = useRef<string[]>([""]);

  const notebookRef = useRef<HTMLDivElement>(null);
  const [taStyle, setTaStyle] = useState<React.CSSProperties>({});

  useLayoutEffect(() => {
    const el = notebookRef.current;
    if (!el) return;
    const update = () => {
      const H = el.offsetHeight;
      const lineH = H * 0.1457;
      const ta = el.querySelector<HTMLTextAreaElement>("textarea");
      const fontSize = ta ? parseFloat(getComputedStyle(ta).fontSize) : 26;
      const halfLeading = (lineH - fontSize) / 2;
      const fontAscender = fontSize * 0.78;
      const paddingTop = Math.max(
        0,
        H * 0.305 - H * 0.06 - halfLeading - fontAscender,
      );
      setTaStyle({ lineHeight: `${lineH}px`, paddingTop: `${paddingTop}px` });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const currentPrompt = prompts[promptIdx];

  // Fetch prompts from API
  const fetchPromptData = useCallback(async () => {
    const data = await getRetrieveContent(apiPaths.RETRIEVE_CONTENT_JOURNAL);
    const list: JournalPrompt[] = data?.data ?? [];
    // Find last index with an answer, start from the next one
    const lastWithAnswer = list.map((p) => !!p.answer).lastIndexOf(true);
    setPromptIdx(lastWithAnswer + 1 < list.length ? lastWithAnswer + 1 : 0);
    setPrompts(list);
  }, [getRetrieveContent]);

  // ── User picks input method → fetch prompts
  const handleMethodPicked = (method: InputMethod) => {
    setInputMethod(method);
    setShowDisplayPopup(false);
    fetchPromptData();
  };

  // ── Text input: enforce max lines (mobile MAX_LINES * CHARS_PER_LINE)
  const handleTextChange = (newText: string) => {
    const lineCountFromChars = Math.ceil(newText.length / CHARS_PER_LINE);
    const lineCountFromBreaks = (newText.match(/\n/g) || []).length + 1;
    const totalLines = Math.max(lineCountFromChars, lineCountFromBreaks);
    if (totalLines > MAX_LINES) {
      const clipped = newText.slice(0, MAX_LINES * CHARS_PER_LINE);
      textHistoryRef.current.push(primaryText);
      setPrimaryText(clipped);
      return;
    }
    textHistoryRef.current.push(primaryText);
    setPrimaryText(newText);
  };

  // ── Undo (mobile: pop history and restore prior text)
  const handleUndo = () => {
    if (!primaryText) return;
    const history = textHistoryRef.current;
    if (history.length > 1) {
      history.pop();
      const previous = history[history.length - 1] ?? "";
      setPrimaryText(previous);
    }
  };

  // ── Save response → POST formdata to captureContentResponse
  const saveResponse = async () => {
    trackEvent(TrackingEvents.journal_saved);
    if (!audioBlob && !primaryText.trim()) {
      setShowCreateWork(true);
      return;
    }
    const formdata = new FormData();
    if (audioBlob) {
      // Web records to webm; backend accepts blob → keep extension truthful
      formdata.append("file", audioBlob, "recording.webm");
    } else {
      formdata.append("answer", primaryText);
    }
    if (!isOwn && currentPrompt?.id) {
      formdata.append("contentId", currentPrompt.id);
    }
    formdata.append(
      "question",
      !isOwn ? (currentPrompt?.question ?? "") : JOURNAL_STRINGS.mythoughts,
    );
    if (currentPrompt?.complexity) {
      formdata.append("complexity", currentPrompt.complexity);
    }
    formdata.append("contentType", "Journal");
    await postRetrieveContent(formdata);
    setShowSuccess(true);
  };

  // ── Navigation guards (mirrors mobile onPress back logic)
  const handleBack = () => {
    if (disableControls || audioBlob || primaryText.trim()) {
      setShowConfirmation(true);
      return;
    }
    navigate(-1);
  };

  const handleNavigateAway = () => {
    audioRecorderRef.current?.stopPlayback();
    navigate(-1);
  };

  // ── Prompt navigation
  const goPrev = () => {
    if (isOwn || promptIdx === 0) return;
    setPromptIdx((i) => Math.max(0, i - 1));
    setAudioBlob(null);
    setAudioUrl("");
  };

  const goNext = () => {
    if (isOwn || promptIdx >= prompts.length - 1) return;
    setPrimaryText("");
    setAudioBlob(null);
    setAudioUrl("");
    setPromptIdx((i) => i + 1);
  };

  const handleShufflePrompts = () => {
    setPrimaryText("");
    setAudioBlob(null);
    setAudioUrl("");
    setDisableControls(true);
    if (!isOwn) fetchPromptData();
    setTimeout(() => setDisableControls(false), 300);
  };

  // ── If answer is text, show it; otherwise our local text
  const displayedText =
    currentPrompt?.answer?.type === "text"
      ? currentPrompt.answer.content
      : primaryText;

  const titleText = isOwn
    ? JOURNAL_STRINGS.mythoughttoday
    : (currentPrompt?.question ?? "");

  const storedAudioUri =
    currentPrompt?.answer?.type === "audio"
      ? `${apiPaths.CONTENT_BASE_URL2}${currentPrompt.answer.content}`
      : undefined;

  // ── Effect: when own-thoughts toggled, clear inputs
  useEffect(() => {
    if (isOwn) {
      setPrimaryText("");
      setAudioBlob(null);
      setAudioUrl("");
    }
  }, [isOwn]);

  return (
    <div className="kj">
      {/* Header: back | question | undo + save (all Lotties, mirrors mobile) */}
      <div className="kj__header">
        <button
          type="button"
          className="kj__icon-btn"
          onClick={handleBack}
          aria-label="Back"
        >
          <Lottie
            animationData={backButtonJson}
            loop={false}
            autoplay={false}
            className="kj__icon"
          />
        </button>

        <h1 className="kj__title">{titleText}</h1>

        <div className="kj__header-right">
          <button
            type="button"
            className="kj__icon-btn"
            onClick={handleUndo}
            disabled={disableControls}
            aria-label="Undo"
          >
            <Lottie
              animationData={undoButtonJson}
              loop={false}
              autoplay={false}
              className="kj__icon"
            />
          </button>
          <button
            type="button"
            className="kj__icon-btn"
            onClick={saveResponse}
            disabled={disableControls}
            aria-label="Save"
          >
            <Lottie
              animationData={saveButtonJson}
              loop={false}
              autoplay={false}
              className="kj__icon"
            />
          </button>
        </div>
      </div>

      {/* Body: notebook background image + Lottie page-tab arrows */}
      <div className="kj__body">
        {/* Left page-tab (prev prompt) — Lottie */}
        <button
          type="button"
          className="kj__tab kj__tab--left"
          onClick={goPrev}
          disabled={disableControls || isOwn || promptIdx === 0}
          aria-label="Previous prompt"
        >
          <Lottie
            animationData={leftArrowJson}
            loop={false}
            autoplay={false}
            className="kj__tab-lottie"
          />
        </button>

        <div
          ref={notebookRef}
          className="kj__notebook"
          style={{ backgroundImage: `url(${notebook})` }}
        >
          <textarea
            className="kj__textarea"
            style={taStyle}
            value={displayedText}
            onChange={(e) => handleTextChange(e.target.value)}
            readOnly={inputMethod !== "write"}
            placeholder={
              inputMethod === "write" ? JOURNAL_STRINGS.startTyping : ""
            }
            rows={MAX_LINES}
            maxLength={MAX_LINES * CHARS_PER_LINE}
          />
        </div>

        {/* Right page-tab (next prompt) — Lottie */}
        <button
          type="button"
          className="kj__tab kj__tab--right"
          onClick={goNext}
          disabled={disableControls || isOwn || promptIdx >= prompts.length - 1}
          aria-label="Next prompt"
        >
          <Lottie
            animationData={rightArrowJson}
            loop={false}
            autoplay={false}
            className="kj__tab-lottie"
          />
        </button>

        {/* Fast-forward (shuffle prompts) — Lottie */}
        <button
          type="button"
          className="kj__tab kj__tab--ff"
          onClick={handleShufflePrompts}
          disabled={disableControls}
          aria-label="Shuffle prompts"
        >
          <Lottie
            animationData={promptForwardJson}
            loop={false}
            autoplay={false}
            className="kj__tab-lottie"
          />
        </button>
      </div>

      {/* Bottom: audio recorder (speak mode) */}
      {inputMethod === "speak" && (
        <div className="kj__recorder">
          <AudioRecorder
            hideTapButton={navigatedFrom === "heartcorner"}
            ref={audioRecorderRef}
            onRecorded={(url, blob) => {
              setAudioUrl(url);
              setAudioBlob(blob);
              setIsShowOwnBtn(false);
            }}
            onRecordingChange={(rec) => {
              setDisableControls(rec);
              if (rec) setIsShowOwnBtn(false);
            }}
            storedAudioUri={storedAudioUri}
            isAnswerTypeText={currentPrompt?.answer?.type === "text"}
            isShowOwnBtn={isShowOwnBtn && !currentPrompt?.answer}
            isOwn={isOwn}
            toggleOwnStory={() => setIsOwn((v) => !v)}
            disableState={disableControls}
            onSave={saveResponse}
          />
        </div>
      )}

      {/* Question audio player (if available) */}
      {currentPrompt?.audio && (
        <div className="kj__question-audio">
          <AudioPlayer uri={currentPrompt.audio} autoPlay variant="compact" />
        </div>
      )}

      {/* Popups */}
      {showDisplayPopup && (
        <DisplayPopUp
          onContinue={handleMethodPicked}
          onClose={handleNavigateAway}
        />
      )}

      {showCreateWork && (
        <CreateWorkPopup
          onOkay={() => setShowCreateWork(false)}
          onClose={() => setShowCreateWork(false)}
        />
      )}

      {showConfirmation && (
        <ConfirmationPopup
          onYes={() => {
            setShowConfirmation(false);
            saveResponse();
          }}
          onNo={() => {
            setShowConfirmation(false);
            handleNavigateAway();
          }}
          onClose={() => setShowConfirmation(false)}
        />
      )}

      {showSuccess && (
        <SuccessPopup
          titleText={JOURNAL_STRINGS.thatsAmazing}
          btnText={JOURNAL_STRINGS.woohoo}
          onPress={() => {
            setShowSuccess(false);
            handleNavigateAway();
          }}
          onClose={handleNavigateAway}
        />
      )}

      {/* Hidden audio element for storing recording URL on the page */}
      {audioUrl && <audio src={audioUrl} style={{ display: "none" }} />}
    </div>
  );
}

export default KidsJournal;
